import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

export type AnalyticsMetricsSnapshot = {
  paidRent: number;
  expectedRent: number;
  rentCollectionRate: number;
  paidDeposit: number;
  expectedDeposit: number;
  depositCollectionRate: number;
  overdueRentAmount: number;
  totalRooms: number;
  occupiedRooms: number;
  occupancyRate: number;
  activeTenantCount: number;
};

export type ActiveTenantExportRow = {
  name: string;
  email: string;
  property: string;
  room: string;
  city: string;
  leaseStart: string;
  leaseEnd: string;
};

const BRAND_TEAL: [number, number, number] = [13, 148, 136];
const TEXT_MUTED: [number, number, number] = [100, 116, 139];
const TEXT_DARK: [number, number, number] = [15, 23, 42];

function formatEur(amount: number): string {
  return `€${amount.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

async function loadPublicImageDataUrl(path: string): Promise<string | null> {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function downloadAnalyticsPdf(opts: {
  metrics: AnalyticsMetricsSnapshot;
  tenants: ActiveTenantExportRow[];
  periodLabel: string;
  propertyScopeLabel: string;
  logoPath?: string;
}): Promise<void> {
  const {
    metrics,
    tenants,
    periodLabel,
    propertyScopeLabel,
    logoPath = "/coliviq-logo.png",
  } = opts;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 16;
  let y = margin;

  // The wordmark carries the brand name, so the header sets no "CoLiviq" type
  // beside it. Width comes from the artwork's own aspect (1600×555) so the mark
  // never stretches; if it fails to load the heading falls back to the name.
  const logoData = await loadPublicImageDataUrl(logoPath);
  const LOGO_H = 9;
  const LOGO_W = LOGO_H * (1600 / 555);
  let drewLogo = false;
  if (logoData) {
    try {
      doc.addImage(logoData, "PNG", margin, y, LOGO_W, LOGO_H);
      drewLogo = true;
    } catch {
      /* ignore bad image */
    }
  }
  if (!drewLogo) {
    doc.setTextColor(...TEXT_DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("coliviq", margin, y + 6);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...TEXT_MUTED);
  doc.text("Analytics & Reporting", margin, y + LOGO_H + 5);

  const exportedAt = new Date().toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  doc.setFontSize(9);
  doc.text(`Generated ${exportedAt}`, pageW - margin, y + 6, {
    align: "right",
  });
  doc.text(`${periodLabel} · ${propertyScopeLabel}`, pageW - margin, y + 11, {
    align: "right",
  });

  y += 22;
  doc.setDrawColor(...BRAND_TEAL);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  if (propertyScopeLabel !== "All properties") {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    const note =
      "Figures below match the dashboard (company-wide). Active tenant rows respect the property filter.";
    const noteLines = doc.splitTextToSize(note, pageW - margin * 2);
    doc.text(noteLines, margin, y);
    y += noteLines.length * 3.6 + 3;
  }

  doc.setTextColor(...TEXT_DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Key metrics", margin, y);
  y += 4;

  const totalRevenue = metrics.paidRent + metrics.paidDeposit;

  autoTable(doc, {
    startY: y,
    head: [["Metric", "Value", "Notes"]],
    body: [
      ["Rent Revenue", formatEur(metrics.paidRent), periodLabel],
      [
        "Rent Collection Rate",
        `${metrics.rentCollectionRate.toFixed(1)}%`,
        `Expected: ${formatEur(metrics.expectedRent)}`,
      ],
      [
        "Overdue Rent",
        formatEur(metrics.overdueRentAmount),
        "Requires attention",
      ],
      ["Total Revenue", formatEur(totalRevenue), "Rent + deposits collected"],
      ["Deposit Revenue", formatEur(metrics.paidDeposit), "Security deposits"],
      [
        "Deposit Collection Rate",
        `${metrics.depositCollectionRate.toFixed(1)}%`,
        `Expected: ${formatEur(metrics.expectedDeposit)}`,
      ],
      [
        "Occupancy Rate",
        `${metrics.occupancyRate.toFixed(1)}%`,
        `${metrics.occupiedRooms} of ${metrics.totalRooms} rooms occupied`,
      ],
      [
        "Active tenants (count)",
        String(metrics.activeTenantCount),
        "Paid rent & deposit, active lease",
      ],
    ],
    theme: "striped",
    headStyles: {
      fillColor: BRAND_TEAL,
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
    },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 52 },
      1: { cellWidth: 38 },
    },
    margin: { left: margin, right: margin },
  });

  const docExt = doc as jsPDF & { lastAutoTable?: { finalY: number } };
  y = (docExt.lastAutoTable?.finalY ?? y) + 12;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...TEXT_DARK);
  doc.text("Active tenants", margin, y);
  y += 4;

  if (tenants.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...TEXT_MUTED);
    doc.text("No active tenants for this scope.", margin, y + 4);
  } else {
    autoTable(doc, {
      startY: y,
      head: [
        [
          "Name",
          "Email",
          "Property",
          "Room",
          "City",
          "Lease start",
          "Lease end",
        ],
      ],
      body: tenants.map((t) => [
        t.name,
        t.email,
        t.property,
        t.room,
        t.city,
        t.leaseStart,
        t.leaseEnd,
      ]),
      theme: "striped",
      headStyles: {
        fillColor: BRAND_TEAL,
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      styles: { fontSize: 8, cellPadding: 2.5, overflow: "linebreak" },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 36 },
        2: { cellWidth: 28 },
        3: { cellWidth: 22 },
        4: { cellWidth: 22 },
        5: { cellWidth: 24 },
        6: { cellWidth: 24 },
      },
      margin: { left: margin, right: margin },
    });
  }

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(
      `coliviq · Page ${i} of ${pageCount}`,
      pageW / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );
  }

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`coliviq-analytics-${stamp}.pdf`);
}

export function downloadAnalyticsExcel(opts: {
  metrics: AnalyticsMetricsSnapshot;
  tenants: ActiveTenantExportRow[];
  periodLabel: string;
  propertyScopeLabel: string;
}): void {
  const { metrics, tenants, periodLabel, propertyScopeLabel } = opts;
  const exportedAt = new Date().toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  const totalRevenue = metrics.paidRent + metrics.paidDeposit;

  const scopeNote =
    propertyScopeLabel !== "All properties"
      ? "Metrics match the dashboard (company-wide); tenant rows use the property filter."
      : "";

  const summaryRows: (string | number)[][] = [
    ["coliviq — Analytics & Reporting"],
    [`Exported: ${exportedAt}`],
    [`Period: ${periodLabel}`],
    [`Scope: ${propertyScopeLabel}`],
    ...(scopeNote ? [[scopeNote], []] : [[]]),
    ["Metric", "Value", "Notes"],
    ["Rent Revenue", formatEur(metrics.paidRent), periodLabel],
    [
      "Rent Collection Rate",
      `${metrics.rentCollectionRate.toFixed(1)}%`,
      `Expected: ${formatEur(metrics.expectedRent)}`,
    ],
    [
      "Overdue Rent",
      formatEur(metrics.overdueRentAmount),
      "Requires attention",
    ],
    ["Total Revenue", formatEur(totalRevenue), "Rent + deposits collected"],
    ["Deposit Revenue", formatEur(metrics.paidDeposit), "Security deposits"],
    [
      "Deposit Collection Rate",
      `${metrics.depositCollectionRate.toFixed(1)}%`,
      `Expected: ${formatEur(metrics.expectedDeposit)}`,
    ],
    [
      "Occupancy Rate",
      `${metrics.occupancyRate.toFixed(1)}%`,
      `${metrics.occupiedRooms} of ${metrics.totalRooms} rooms occupied`,
    ],
    [
      "Active tenants (count)",
      metrics.activeTenantCount,
      "Paid rent & deposit, active lease",
    ],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
  const summaryMerges: XLSX.Range[] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: 2 } },
  ];
  if (scopeNote) {
    summaryMerges.push({ s: { r: 4, c: 0 }, e: { r: 4, c: 2 } });
  }
  wsSummary["!merges"] = summaryMerges;

  const tenantHeaders = [
    "Name",
    "Email",
    "Property",
    "Room",
    "City",
    "Lease start",
    "Lease end",
  ];
  const tenantRows = tenants.map((t) => [
    t.name,
    t.email,
    t.property,
    t.room,
    t.city,
    t.leaseStart,
    t.leaseEnd,
  ]);

  const wsTenants = XLSX.utils.aoa_to_sheet([
    ["Active tenants"],
    [`Exported: ${exportedAt}`],
    [],
    tenantHeaders,
    ...tenantRows,
  ]);
  wsTenants["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: tenantHeaders.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: tenantHeaders.length - 1 } },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
  XLSX.utils.book_append_sheet(wb, wsTenants, "Active tenants");

  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `coliviq-analytics-${stamp}.xlsx`);
}
